import { useCallback } from 'react';
import { useExportModalStore } from '@/features/interactions/addToCalendar/hooks/useExportModalStore';
import { type LaunchOption, useLauncherAction } from '@/hooks/useLauncher';
import { useQueryParams } from '@/hooks/useQueryParams';
import { PlatformTypes } from '@/types/gql.generated';

type Arg = {
  launch: LaunchOption;
};

// Resumes the add-to-Outlook workflow after the OAuth round trip. The launch
// key (outlook-add:<eventId|all>) carries the target, so on return we reopen the
// shared export modal on the Outlook platform — which now shows the connected
// calendars. Mirrors addToGoogle's useExportModalLaunch.
export const useOutlookExportModalLaunch = ({ launch }: Arg) => {
  const { queryParams, removeQueryParam } = useQueryParams();
  const { setOpenPlatform, setIsModalWorkflow, openPlatform } =
    useExportModalStore();

  const target = launch?.split(':')[1] || null;
  const eventId = target === 'all' ? null : target;
  const isRejected = queryParams.get('code') === 'PERMISSION_NOT_GRANTED';

  const handleLauncherAction = useCallback(() => {
    if (isRejected) {
      removeQueryParam('code');
      return;
    }
    setOpenPlatform(PlatformTypes.OutlookIntegration, eventId);
    setIsModalWorkflow(true);
  }, [
    isRejected,
    removeQueryParam,
    setOpenPlatform,
    setIsModalWorkflow,
    eventId,
  ]);

  useLauncherAction(launch, handleLauncherAction);

  return { openPlatform };
};
